// src/features/admin/components/SchoolsTableSection.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Edit2, Save, X, Trash2, Copy, ChevronUp, ChevronDown, Plus } from "lucide-react";
import { toast } from "react-toastify";

/* =========================
 * Voucher Modal (same UX as UsersManager)
 * ========================= */
function VouchersModal({ open, onClose, title, vouchers }) {
  if (!open) return null;

  const list = Array.isArray(vouchers) ? vouchers.filter(Boolean) : [];

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Tersalin ke clipboard");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  const copyAll = () => {
    if (!list.length) return;
    copyText(list.join(", "));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Daftar Voucher</h3>
            <p className="text-sm text-gray-500">{title || "Voucher"}</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold">{list.length}</span>
            </p>

            <button
              type="button"
              onClick={copyAll}
              disabled={!list.length}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                list.length
                  ? "bg-gray-800 text-white hover:bg-gray-900"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
              title={list.length ? "Salin semua voucher" : "Tidak ada voucher"}
            >
              <Copy className="w-4 h-4" />
              Copy all
            </button>
          </div>

          {list.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
              Belum ada voucher untuk sekolah/distributor ini.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {list.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => copyText(v)}
                  className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  title="Klik untuk menyalin voucher"
                >
                  <span className="font-mono">{v}</span>
                  <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-700" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================
 * Voucher cell (same logic as UsersManager)
 * ========================= */
function VoucherCell({ codes, onOpenModal }) {
  const list = Array.isArray(codes) ? codes.filter(Boolean) : [];
  const count = list.length;

  const copyVoucher = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Voucher tersalin");
    } catch {
      toast.error("Gagal menyalin");
    }
  };

  if (count === 0) return <span className="text-xs text-gray-400">-</span>;

  if (count === 1) {
    const v = list[0];
    return (
      <button
        type="button"
        onClick={() => copyVoucher(v)}
        className="group inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
        title="Klik untuk menyalin voucher"
      >
        <span className="font-mono">{v}</span>
        <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-700" />
      </button>
    );
  }

  const first = list[0];

  return (
    <button
      type="button"
      onClick={onOpenModal}
      className="relative inline-flex items-center"
      title="Klik untuk melihat semua voucher"
    >
      <span className="absolute inset-0 translate-x-[-4px] translate-y-[3px] rounded-full border border-gray-200 bg-white opacity-60" />
      <span className="absolute inset-0 translate-x-[-2px] translate-y-[1.5px] rounded-full border border-gray-200 bg-white opacity-80" />

      <span className="relative inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
        <span className="font-mono">{first}</span>
        <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 px-2 py-[1px] text-[10px] font-semibold text-blue-700 border border-blue-100">
          +{count - 1}
        </span>
      </span>
    </button>
  );
}

function getVoucherCodes(school) {
  // supports: vouchers: [{code: "ABC"}] OR vouchers: ["ABC"]
  const raw = Array.isArray(school?.vouchers) ? school.vouchers : [];
  return raw
    .map((v) => (typeof v === "string" ? v : v?.code))
    .filter(Boolean);
}

export default function SchoolsTableSection({
  schools,
  onUpdate,
  onDelete,
  showAddForm,
  setShowAddForm,
  onAdd,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [newSchool, setNewSchool] = useState({ name: "", code: "" });

  const [sortKey, setSortKey] = useState("name"); // default alphabet by name
  const [sortDir, setSortDir] = useState("asc");

  const [showVouchersModal, setShowVouchersModal] = useState(false);
  const [voucherModalTitle, setVoucherModalTitle] = useState("");
  const [voucherModalCodes, setVoucherModalCodes] = useState([]);

  /* =========================
   * ✅ Pagination state (client-side)
   * ========================= */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // 10 | 25 | 50 | "all"

  // Reset to first page when data changes (e.g., searchTerm in parent changes)
  useEffect(() => {
    setPage(1);
  }, [schools, sortKey, sortDir]);

  const toggleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey]
  );

  const sortedSchools = useMemo(() => {
    const arr = Array.isArray(schools) ? [...schools] : [];
    const dir = sortDir === "asc" ? 1 : -1;

    const getVal = (s) => {
      if (sortKey === "name") return String(s?.name || "");
      if (sortKey === "code") return String(s?.code || "");
      if (sortKey === "voucher") {
        const codes = getVoucherCodes(s);
        return String(codes[0] || "");
      }
      return "";
    };

    arr.sort((a, b) => getVal(a).localeCompare(getVal(b), "id", { sensitivity: "base" }) * dir);
    return arr;
  }, [schools, sortKey, sortDir]);

  const total = sortedSchools.length;

  const totalPages = useMemo(() => {
    if (pageSize === "all") return 1;
    const ps = Math.max(Number(pageSize) || 10, 1);
    return Math.max(Math.ceil(total / ps), 1);
  }, [total, pageSize]);

  // Keep page in range if total shrinks
  useEffect(() => {
    setPage((p) => Math.min(Math.max(p, 1), totalPages));
  }, [totalPages]);

  const visibleSchools = useMemo(() => {
    if (pageSize === "all") return sortedSchools;
    const ps = Math.max(Number(pageSize) || 10, 1);
    const start = (Math.max(page, 1) - 1) * ps;
    const end = start + ps;
    return sortedSchools.slice(start, end);
  }, [sortedSchools, page, pageSize]);

  const fromToText = useMemo(() => {
    if (total === 0) return "Menampilkan 0 data";
    if (pageSize === "all") return `Menampilkan 1-${total} dari ${total} data`;

    const ps = Math.max(Number(pageSize) || 10, 1);
    const start = (page - 1) * ps + 1;
    const end = Math.min(page * ps, total);
    return `Menampilkan ${start}-${end} dari ${total} data`;
  }, [total, page, pageSize]);

  const openVoucherModalForSchool = (school) => {
    const codes = getVoucherCodes(school);
    setVoucherModalTitle(school?.name ? `Sekolah/Distributor: ${school.name}` : "Voucher");
    setVoucherModalCodes(codes);
    setShowVouchersModal(true);
  };

  const handleEdit = (school) => {
    setEditingId(school._id);
    setEditForm({ _id: school._id, name: school.name || "", code: school.code || "" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleSaveEdit = async () => {
    if (!editForm?.name?.trim() || !editForm?.code?.trim()) {
      toast.error("Nama dan kode harus diisi");
      return;
    }
    await onUpdate(editForm._id, { name: editForm.name.trim(), code: editForm.code.trim() });
    handleCancelEdit();
  };

  const handleAddSchool = async () => {
    if (!newSchool.name.trim() || !newSchool.code.trim()) {
      toast.error("Nama dan kode sekolah/distributor harus diisi");
      return;
    }
    await onAdd({ name: newSchool.name.trim(), code: newSchool.code.trim() });
    setNewSchool({ name: "", code: "" });
  };

  const SortIcon = ({ active, dir }) => {
    if (!active)
      return (
        <span className="inline-flex opacity-30">
          <ChevronUp className="w-4 h-4" />
        </span>
      );
    return dir === "asc" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  if (showAddForm) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg text-gray-900">Tambah Sekolah/Distributor Baru</h3>
          <button
            type="button"
            onClick={() => {
              setShowAddForm(false);
              setNewSchool({ name: "", code: "" });
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nama Sekolah/Distributor</label>
            <input
              type="text"
              value={newSchool.name}
              onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
              placeholder="e.g., Distributor test"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Kode</label>
            <input
              type="text"
              value={newSchool.code}
              onChange={(e) => setNewSchool({ ...newSchool, code: e.target.value.toUpperCase() })}
              placeholder="e.g., DISTRIBU5081"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleAddSchool}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4F7CFF] text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all"
            >
              <Plus className="w-4 h-4" />
              Tambah
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewSchool({ name: "", code: "" });
              }}
              className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all"
            >
              Batal
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!sortedSchools || sortedSchools.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">Tidak ada sekolah/distributor yang ditemukan</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-gray-700">
              <th className="px-6 py-4 text-left font-medium">
                <button
                  type="button"
                  onClick={() => toggleSort("name")}
                  className="inline-flex items-center gap-2 hover:text-gray-900"
                  title="Sort by Nama"
                >
                  Nama <SortIcon active={sortKey === "name"} dir={sortDir} />
                </button>
              </th>

              <th className="px-6 py-4 text-left font-medium">
                <button
                  type="button"
                  onClick={() => toggleSort("code")}
                  className="inline-flex items-center gap-2 hover:text-gray-900"
                  title="Sort by Kode"
                >
                  Kode <SortIcon active={sortKey === "code"} dir={sortDir} />
                </button>
              </th>

              <th className="px-6 py-4 text-left font-medium">
                <button
                  type="button"
                  onClick={() => toggleSort("voucher")}
                  className="inline-flex items-center gap-2 hover:text-gray-900"
                  title="Sort by Voucher"
                >
                  Voucher <SortIcon active={sortKey === "voucher"} dir={sortDir} />
                </button>
              </th>

              <th className="px-6 py-4 text-center font-medium">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {visibleSchools.map((school) => {
              const codes = getVoucherCodes(school);

              return (
                <tr key={school._id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    {editingId === school._id ? (
                      <input
                        type="text"
                        value={editForm?.name || ""}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
                      />
                    ) : (
                      <span className="font-medium text-gray-900">{school.name}</span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    {editingId === school._id ? (
                      <input
                        type="text"
                        value={editForm?.code || ""}
                        onChange={(e) => setEditForm((p) => ({ ...p, code: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
                      />
                    ) : (
                      <span className="inline-block px-2.5 py-1 bg-blue-50 text-[#4F7CFF] text-xs font-medium rounded-md">
                        {school.code}
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <VoucherCell codes={codes} onOpenModal={() => openVoucherModalForSchool(school)} />
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      {editingId === school._id ? (
                        <>
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#4F7CFF] text-white text-xs font-medium hover:bg-blue-600 transition-all"
                          >
                            <Save className="w-4 h-4" />
                            Simpan
                          </button>

                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50 transition-all"
                          >
                            <X className="w-4 h-4" />
                            Batal
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => handleEdit(school)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-xs font-medium hover:bg-gray-50 transition-all"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(school._id)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                            Hapus
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-white">
        <div className="text-xs text-gray-500">{fromToText}</div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span>Tampilkan</span>
            <select
              value={String(pageSize)}
              onChange={(e) => {
                const val = e.target.value === "all" ? "all" : Number(e.target.value);
                setPageSize(val);
                setPage(1);
              }}
              className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#4F7CFF]"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="all">Semua</option>
            </select>
            <span>per halaman</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page <= 1}
              className={`h-8 w-8 inline-flex items-center justify-center rounded-md border ${
                page <= 1 ? "border-gray-200 text-gray-300 cursor-not-allowed" : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              title="Sebelumnya"
            >
              ‹
            </button>

            <div className="text-xs text-gray-600">
              Halaman <span className="font-semibold">{page}</span> dari{" "}
              <span className="font-semibold">{totalPages}</span>
            </div>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page >= totalPages}
              className={`h-8 w-8 inline-flex items-center justify-center rounded-md border ${
                page >= totalPages
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              title="Berikutnya"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <VouchersModal
        open={showVouchersModal}
        onClose={() => {
          setShowVouchersModal(false);
          setVoucherModalCodes([]);
          setVoucherModalTitle("");
        }}
        title={voucherModalTitle}
        vouchers={voucherModalCodes}
      />
    </div>
  );
}
